import { Stack, Typography } from '@mui/material';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import { theme } from '../../../../theme/ThemeProvider';
import { CardStatus } from '../../../../components/common/Card/CardStatus/CardStatus';
import { ChosenConnectorProps } from './types';
import { useTranslation } from 'react-i18next';

export const ChosenConnector: React.FC<ChosenConnectorProps> = ({
  stationPrice,
  connectorId,
  connectorStatus,
  downtimePricePerMinute,
  downtimeFreeMinutes,
  premiumPricePerKwh,
}) => {
  const { t } = useTranslation();
  const showDowntime =
    typeof downtimePricePerMinute === 'number' && downtimePricePerMinute > 0;
  const hasPremiumPrice = typeof premiumPricePerKwh === 'number';
  const premiumColor = theme.palette.orange.main;
  const freeMinutes = downtimeFreeMinutes ?? 0;
  const energyUnit = t('orderPage.energyUnit');
  return (
    <Stack
      sx={{
        width: '100%',
        gap: '10px',
        borderRadius: '15px',
        border: hasPremiumPrice
          ? `1px solid ${premiumColor}55`
          : `1px solid ${theme.palette.gray[50]}`,
        // The premium variant had a surface and the standard one only an
        // outline, so the same card read as two different components. Both sit
        // on a surface now; the gradient stays as the premium signal.
        background: hasPremiumPrice
          ? `linear-gradient(135deg, ${premiumColor}1A 0%, ${premiumColor}05 100%)`
          : 'rgba(255, 255, 255, 0.04)',
        padding: '12px',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ gap: '8px' }}
      >
        <Typography
          variant="body1"
          color="common.white"
          sx={{ fontWeight: 600, minWidth: 0 }}
        >
          {t('connectorCard.connector')} {connectorId}
        </Typography>
        <CardStatus
          chargingStatus={connectorStatus}
          text="statuses.connector"
        />
      </Stack>

      <Stack sx={{ gap: '2px', minWidth: 0 }}>
        {hasPremiumPrice ? (
          <Stack direction="row" alignItems="center" sx={{ gap: '6px' }}>
            <WorkspacePremiumRoundedIcon
              sx={{ fontSize: 16, color: premiumColor }}
            />
            <Typography
              variant="body2"
              sx={{
                color: premiumColor,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontSize: '11px',
                lineHeight: 1,
              }}
            >
              {t('orderPage.yourPremiumPrice')}
            </Typography>
          </Stack>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.gray[50],
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontSize: '11px',
              lineHeight: 1,
            }}
          >
            {t('orderPage.pricing')}
          </Typography>
        )}
        <Stack direction="row" alignItems="baseline" sx={{ gap: '8px' }}>
          <Typography
            variant="body1"
            color="common.white"
            // The one number the driver came to check — it was set at the same
            // size as the label above it and the address above that.
            sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '20px' }}
          >
            {hasPremiumPrice ? premiumPricePerKwh : stationPrice} ₴/
            {energyUnit}
          </Typography>
          {hasPremiumPrice && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.gray[50],
                textDecoration: 'line-through',
                fontSize: '12px',
              }}
            >
              {stationPrice} ₴/{energyUnit}
            </Typography>
          )}
        </Stack>
      </Stack>

      {showDowntime && (
        <Stack
          direction="row"
          alignItems="flex-start"
          sx={{
            gap: '6px',
            borderTop: `1px dashed ${theme.palette.gray[50]}`,
            paddingTop: '10px',
          }}
        >
          <AccessTimeRoundedIcon
            sx={{ fontSize: 14, color: 'gray.50', marginTop: '2px' }}
          />
          <Typography variant="body2" color="gray.50" sx={{ fontSize: '12px' }}>
            <Typography
              component="span"
              variant="body2"
              sx={{ fontSize: '12px', fontWeight: 700, color: 'common.white' }}
            >
              {t('orderPage.downtimeTitle')}
            </Typography>
            {' · '}
            {freeMinutes > 0
              ? t('orderPage.downtimeNotice', {
                  free: freeMinutes,
                  price: downtimePricePerMinute.toFixed(2),
                })
              : t('orderPage.downtimeNoticeNoFree', {
                  price: downtimePricePerMinute.toFixed(2),
                })}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};
